import { ChevronLeft, ChevronRight, Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { AgentChip } from "../AgentChip";
import { formatEuro } from "../gulaConstants";
import { MoneyTabs } from "../money/MoneyTabs";
import {
  callsByDay,
  closingRate,
  logLabel,
  madridDay,
  periodLabel,
  periodOf,
  shiftPeriod,
  sortStats,
  visibleStats,
  type Period,
  type SetterStats,
  type StatsScope,
} from "./setterStats";
import { useSetterLog, useSetterStats } from "./useSetterStats";

const SCOPES: { value: StatsScope; label: string }[] = [
  { value: "day", label: "Día" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
];

const COLUMNS: {
  key: keyof SetterStats | "rate" | "commission";
  label: string;
  title?: string;
}[] = [
  { key: "calls", label: "Llamadas" },
  { key: "talked", label: "Hablados", title: "El lead cogió el teléfono" },
  { key: "first_contact", label: "1er cont." },
  { key: "second_contact", label: "2º cont." },
  { key: "sure_purchase", label: "Cierre", title: "Pasados a Compra segura" },
  { key: "sales", label: "Compras" },
  { key: "rate", label: "Cierre %", title: "Compras entre hablados" },
  {
    key: "welcome_calls",
    label: "Bienvenida",
    title: "Llamadas de bienvenida hechas tras la instalación",
  },
  {
    key: "maintenances",
    label: "Mantenim.",
    title: "Mantenimientos contratados tras su llamada",
  },
  { key: "no_answer", label: "No cont." },
  { key: "lost", label: "No compra" },
  { key: "leads_taken", label: "Leads", title: "Leads cogidos en el periodo" },
  {
    key: "avg_first_call_hours",
    label: "1ª llamada",
    title: "Horas de media desde que entra el lead hasta su primera llamada",
  },
  { key: "points", label: "Puntos" },
  {
    key: "commission",
    label: "Comisión",
    title:
      "Del libro de comisiones: 20 € por venta que cumple la regla, 5 € por mantenimiento",
  },
];

const cell = (row: SetterStats, key: (typeof COLUMNS)[number]["key"]) => {
  if (key === "rate") {
    const rate = closingRate(row);
    return rate == null ? "—" : `${rate} %`;
  }
  if (key === "commission") return formatEuro(row.commission);
  if (key === "avg_first_call_hours") {
    return row.avg_first_call_hours == null
      ? "—"
      : `${row.avg_first_call_hours.toLocaleString("es-ES")} h`;
  }
  return row[key];
};

const hourOf = (timestamp: string) =>
  new Date(timestamp).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Madrid",
  });

const dayOf = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString("es-ES", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

/** What one person did in the period: calls per day and the list of calls. */
const SetterDetail = ({
  row,
  period,
}: {
  row: SetterStats;
  period: Period;
}) => {
  const { data: log = [], isPending } = useSetterLog(String(row.id), period);
  const days = callsByDay(log, period);
  const maxCalls = Math.max(1, ...days.map((day) => day.calls));

  return (
    <Card className="gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-semibold">
          <AgentChip id={row.id} name={row.first_name} compact />
          {row.first_name} {row.last_name ?? ""}
          {row.administrator ? (
            <span className="text-xs font-normal text-muted-foreground">
              administrador
            </span>
          ) : null}
        </h2>
        <span className="text-xs text-muted-foreground">
          {row.active_days} {row.active_days === 1 ? "día" : "días"} con
          llamadas · {row.calls} llamadas · {row.sales} compras
        </span>
      </div>

      {days.length > 1 ? (
        <div className="flex items-end gap-1" aria-label="Llamadas por día">
          {days.map((day) => (
            <div
              key={day.day}
              className="flex flex-1 flex-col items-center gap-1"
              title={`${dayOf(day.day)}: ${day.calls} llamadas, ${day.talked} hablados, ${day.sales} compras`}
            >
              <div className="flex h-16 w-full items-end justify-center">
                <div
                  className={cn(
                    "w-full max-w-6 rounded-t",
                    day.sales ? "bg-lime-400" : "bg-primary/70",
                  )}
                  style={{
                    height: `${(day.calls / maxCalls) * 100}%`,
                    minHeight: day.calls ? 3 : 0,
                  }}
                />
              </div>
              {days.length <= 14 ? (
                <span className="text-[10px] text-muted-foreground">
                  {Number(day.day.slice(8, 10))}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {isPending ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : log.length ? (
        <div className="max-h-[28rem] overflow-y-auto rounded-lg border">
          <table className="w-full text-sm">
            <tbody>
              {log.map((entry, index) => {
                const newDay = index === 0 || log[index - 1].day !== entry.day;
                return (
                  <tr
                    key={String(entry.id)}
                    className="border-t first:border-0"
                  >
                    <td className="w-24 px-3 py-1.5 whitespace-nowrap text-xs text-muted-foreground">
                      {newDay && period.scope !== "day" ? (
                        <span className="font-medium text-foreground">
                          {dayOf(entry.day)}
                        </span>
                      ) : null}
                    </td>
                    <td className="w-14 px-2 py-1.5 tabular-nums text-muted-foreground">
                      {hourOf(entry.created_at)}
                    </td>
                    <td className="px-2 py-1.5">
                      {entry.client_id ? (
                        <Link
                          to={`/clientes?cliente=${entry.client_id}`}
                          className="hover:underline"
                        >
                          {[entry.first_name, entry.last_name]
                            .filter(Boolean)
                            .join(" ") || "Cliente"}
                        </Link>
                      ) : (
                        "—"
                      )}
                      {entry.city ? (
                        <span className="text-xs text-muted-foreground">
                          {" "}
                          · {entry.city}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-2 py-1.5">
                      <span
                        className={cn(
                          "rounded-full px-2 py-0.5 text-xs",
                          entry.outcome === "sale"
                            ? "bg-lime-200 text-neutral-900"
                            : entry.outcome === "no_answer"
                              ? "bg-muted text-muted-foreground"
                              : "bg-sky-100 text-sky-900 dark:bg-sky-400/15 dark:text-sky-200",
                        )}
                      >
                        {logLabel(entry)}
                        {!entry.is_call ? " (sin llamada)" : ""}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                      +{entry.points}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          Sin llamadas en este periodo.
        </p>
      )}
    </Card>
  );
};

/** Admins: what every setter is doing, by day, week or month. */
export const SettersPage = () => {
  const today = madridDay(new Date());
  const [period, setPeriod] = useState<Period>(() => periodOf("day", today));
  const [selected, setSelected] = useState<string | null>(null);
  const { data = [], isPending, error } = useSetterStats(period);
  const rows = sortStats(visibleStats(data));
  const selectedRow = rows.find((row) => String(row.id) === selected);
  const isCurrent = periodOf(period.scope, today).from === period.from;

  const totals = rows.reduce(
    (sum, row) => ({
      calls: sum.calls + row.calls,
      talked: sum.talked + row.talked,
      sales: sum.sales + row.sales,
      commission: sum.commission + row.commission,
    }),
    { calls: 0, talked: 0, sales: 0, commission: 0 },
  );

  return (
    <div className="flex flex-col gap-5 py-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Users className="h-6 w-6" />
            Setters
          </h1>
          <p className="text-sm text-muted-foreground">
            Llamadas, contactos, cierres y compras de cada persona. Pulsa una
            fila para ver sus llamadas una a una.
          </p>
        </div>
        <MoneyTabs />
        <div className="flex w-full flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-lg bg-muted p-1 text-sm">
            {SCOPES.map((scope) => (
              <button
                key={scope.value}
                type="button"
                onClick={() => setPeriod(periodOf(scope.value, today))}
                className={cn(
                  "rounded-md px-3 py-1 font-medium",
                  period.scope === scope.value
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {scope.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 rounded-lg border px-1 py-0.5 text-sm">
            <button
              type="button"
              onClick={() => setPeriod(shiftPeriod(period, -1))}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Periodo anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-28 text-center font-medium first-letter:uppercase">
              {periodLabel(period, today)}
            </span>
            <button
              type="button"
              disabled={isCurrent}
              onClick={() => setPeriod(shiftPeriod(period, 1))}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
              aria-label="Periodo siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-destructive">
          No se han podido cargar los datos del equipo.
        </p>
      ) : isPending ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full min-w-[980px] text-sm">
            <thead className="bg-muted/50 text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Persona</th>
                {COLUMNS.map((column) => (
                  <th
                    key={column.key}
                    className="px-2 py-2 text-right font-medium"
                    title={column.title}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isSelected = String(row.id) === selected;
                return (
                  <tr
                    key={String(row.id)}
                    onClick={() =>
                      setSelected(isSelected ? null : String(row.id))
                    }
                    className={cn(
                      "cursor-pointer border-t hover:bg-muted/40",
                      isSelected && "bg-muted/60",
                    )}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 font-medium">
                        <AgentChip id={row.id} name={row.first_name} compact />
                        {row.first_name}
                        {row.administrator ? (
                          <span className="text-xs font-normal text-muted-foreground">
                            admin
                          </span>
                        ) : null}
                      </div>
                    </td>
                    {COLUMNS.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          "px-2 py-2 text-right tabular-nums",
                          column.key === "sales" && row.sales
                            ? "font-semibold"
                            : "",
                          column.key === "points" ? "font-semibold" : "",
                        )}
                      >
                        {cell(row, column.key)}
                      </td>
                    ))}
                  </tr>
                );
              })}
              {!rows.length ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length + 1}
                    className="px-3 py-8 text-center text-muted-foreground"
                  >
                    Nadie ha llamado en este periodo.
                  </td>
                </tr>
              ) : null}
            </tbody>
            {rows.length > 1 ? (
              <tfoot className="border-t bg-muted/30 text-xs font-medium">
                <tr>
                  <td className="px-3 py-2">Total</td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {totals.calls}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    {totals.talked}
                  </td>
                  <td colSpan={3} />
                  <td className="px-2 py-2 text-right tabular-nums">
                    {totals.sales}
                  </td>
                  <td colSpan={7} />
                  <td />
                  <td className="px-2 py-2 text-right tabular-nums">
                    {formatEuro(totals.commission)}
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}

      {selectedRow ? <SetterDetail row={selectedRow} period={period} /> : null}

      <p className="text-xs text-muted-foreground">
        Comisión: 20 € por venta con lead suyo y hablado en los 30 días
        anteriores, 5 € por mantenimiento cerrado en la llamada de bienvenida.
        El detalle y los pagos están en la pestaña Comisiones. Puntos: venta 10
        · mantenimiento 5 · hablado 3 · recontacto 3 · bienvenida 3 · no compra
        2 · no contesta 1.
      </p>
    </div>
  );
};

SettersPage.path = "/dinero/setters";
